import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardProgressBar,
  WizardStepIndicator,
} from '@/components/wizards';
import { useGetSubscription } from '@/hooks/useSubscription';
import { SeedTypeOption } from '@/types';

import type {
  AddSeedByTypeModalProps,
  SeedWizardFormData,
} from './AddSeedByTypeModal.types';
import { useAddSeedSubmit } from './hooks';
import {
  ConfigureAuthStep,
  DataEntryStep,
  SubmitProgressStep,
  TypeSelectionStep,
} from './steps';
import {
  DEFAULT_SEED_FORM_DATA,
  seedWizardSteps,
  STEP_IDS,
} from './wizardConfig';

/**
 * AddSeedByTypeModal - A wizard-based modal for adding seeds by type
 *
 * Simplified to only handle text input - file imports are now handled
 * via the ImportSeedsModal flow.
 *
 * ## Architecture Decisions
 * - Wizard handles navigation and form data (seedType, textInput)
 * - File imports delegated to ImportSeedsModal (opened via onOpenImport)
 * - canSubmit validation checks for non-empty text input
 * - Subscription validation intercepts handleNext to block locked features
 * - On submit, the body switches to a progress view (seeds → credentials)
 *   and the footer collapses to a single Close button.
 */
export const AddSeedByTypeModal = ({
  open,
  onClose,
  onSuccess,
  onOpenImport,
}: AddSeedByTypeModalProps) => {
  const { data: subscription } = useGetSubscription();
  const isWebApplicationsLocked =
    !subscription?.attackSurface?.applications ||
    subscription.numberOfApplications === 0;

  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null
  );

  const wizard = useWizard<SeedWizardFormData>({
    steps: seedWizardSteps,
    initialData: DEFAULT_SEED_FORM_DATA,
  });

  const {
    handleSubmit,
    isPending,
    isComplete,
    seedsStatus,
    credentialsStatus,
    error,
    reset: resetSubmit,
  } = useAddSeedSubmit({
    formData: wizard.formData,
    onSuccess,
  });

  const handleClose = () => {
    wizard.reset();
    resetSubmit();
    setSubscriptionError(null);
    onClose();
  };

  const handleOpenImport = () => {
    if (onOpenImport) {
      handleClose();
      onOpenImport();
    }
  };

  // Single consolidated reset effect on modal open
  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (open) {
      wizard.reset();
      const timer = setTimeout(() => {
        setSubscriptionError(null);
        resetSubmit();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Clear text when seed type changes
  useEffect(() => {
    wizard.setFormData(prev => ({ ...prev, textInput: '' }));
  }, [wizard.formData.seedType]);

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    wizard.setFormData(prev => ({ ...prev, textInput: e.target.value }));
  };

  const handleNextWithSubscriptionCheck = () => {
    const { seedType } = wizard.formData;

    if (
      (seedType === SeedTypeOption.WEB_APPLICATION ||
        seedType === SeedTypeOption.WEB_SERVICE) &&
      isWebApplicationsLocked
    ) {
      setSubscriptionError(
        'Please contact your MSP to enable Web testing in your subscription'
      );
      return;
    }

    setSubscriptionError(null);
    wizard.navigation.handleNext();
  };

  const canSubmit = useMemo(() => {
    const hasText = wizard.formData.textInput.trim().length > 0;
    return hasText && !isPending;
  }, [wizard.formData.textInput, isPending]);

  const footerConfig = useWizardFooterConfig({
    canGoNext:
      wizard.currentStepId === STEP_IDS.SELECT_TYPE
        ? wizard.navigation.canGoNext
        : canSubmit,
    canGoBack: wizard.navigation.canGoBack,
    isPending,
    isLastStep: wizard.navigation.isLastStep,
    onNext: handleNextWithSubscriptionCheck,
    onBack: wizard.navigation.handleBack,
    onCancel: handleClose,
    onSubmit: handleSubmit,
    labels: { submit: 'Add Seeds', submitting: 'Adding...' },
  });

  // Once the user clicks Add Seeds, swap the body for the progress view and
  // the footer for a single Close button. The progress view stays mounted
  // until the modal is closed (so the user sees the final checkmarks).
  const isSubmitView = isPending || isComplete;

  const renderCurrentStep = () => {
    if (isSubmitView) {
      return (
        <SubmitProgressStep
          seedType={wizard.formData.seedType}
          seedsStatus={seedsStatus}
          credentialsStatus={credentialsStatus}
        />
      );
    }

    const commonProps = {
      formData: wizard.formData,
      setFormData: wizard.setFormData,
      isSubmitting: isPending,
    };

    switch (wizard.currentStepId) {
      case STEP_IDS.SELECT_TYPE:
        return (
          <TypeSelectionStep
            {...commonProps}
            subscriptionError={subscriptionError}
          />
        );

      case STEP_IDS.ADD_SEEDS:
        return (
          <DataEntryStep
            {...commonProps}
            onTextChange={handleTextChange}
            onOpenImport={handleOpenImport}
          />
        );

      case STEP_IDS.CONFIGURE_AUTH:
        return <ConfigureAuthStep {...commonProps} />;

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <div className="flex w-full items-baseline justify-between">
          <span>Add Seeds</span>
          <WizardStepIndicator
            currentStep={wizard.progress.currentDisplayStep}
            totalSteps={wizard.progress.totalDisplaySteps}
            stepTitle={wizard.progress.currentStepTitle}
          />
        </div>
      }
      size="xl"
      open={open}
      onClose={handleClose}
      hideFooter={true}
    >
      <div className="flex flex-col gap-4">
        <WizardProgressBar
          currentStep={wizard.progress.currentDisplayStep}
          totalSteps={wizard.progress.totalDisplaySteps}
          stepTitle={wizard.progress.currentStepTitle}
        />

        {renderCurrentStep()}

        {isSubmitView && error && (
          <div className="border-chariot-stroke bg-chariot-background-secondary mt-2 rounded-md border p-3 pl-4">
            <div className="text-chariot-stroke-critical text-xs">
              {error.message}
            </div>
          </div>
        )}
      </div>

      {isSubmitView ? (
        <SubmitDoneFooter disabled={!isComplete} onClose={handleClose} />
      ) : (
        <WizardModalFooter config={footerConfig} />
      )}
    </Modal>
  );
};

interface SubmitDoneFooterProps {
  disabled: boolean;
  onClose: () => void;
}

function SubmitDoneFooter({ disabled, onClose }: SubmitDoneFooterProps) {
  return (
    <div className="mt-6 flex items-center justify-end pt-6 pb-5">
      <Button
        onClick={onClose}
        disabled={disabled}
        className="min-w-24 px-4"
        data-testid="wizard-close-button"
      >
        Close
      </Button>
    </div>
  );
}
